/* ============================================================
   EMPRESS WISHLIST (Oct 2026, replaces Swym Wishlist Plus)
   One heart. Saves the product, with her size only when she has
   actually picked one (Swym saved every heart as XS, the first
   variant, because the size gate leaves the form on XS).
   Signed out: the list lives in this browser.
   Signed in: it lives on her account through the planner
   (/apps/empress-fit/wishlist, Shopify app proxy, signed by
   Shopify), so it follows her to any device.

   Markup this script looks for:
     Heart:   <button data-ee-wish data-product-id data-handle
                      [data-source] [data-variant-id] [data-form-id]>
     Count:   [data-ee-wish-count]
     Cart:    <button data-ee-wish-later data-product-id
                      data-variant-id data-handle data-remove-url>
   Page:      assets/wishlist-page.js (sections/wishlist-page.liquid)
   API:       window.EEWish  (items, has, get, add, remove, setSize, ready)
   Event:     document 'ee:wishlist' { items }
   Words:     window.EEWishConfig.text (Theme settings > Empress wishlist)
   ============================================================ */
(function () {
'use strict';
if (window.EEWish) return;

var cfg = window.EEWishConfig || {};
var TEXT = cfg.text || {};
var KEY = 'ee:wl:v1';
var ANON = 'ee:wl:anon';
var API = '/apps/empress-fit/wishlist';
var PAGE = cfg.page || '/pages/wishlist';
var ME = cfg.customer ? String(cfg.customer) : null;
var STALE_MS = 5 * 60 * 1000;
var MAX = 200;

function t(key, fallback) { return TEXT[key] || fallback; }

/* ---------- storage ----------
   Always re-read before a change: a page restored by the Back button (or another tab)
   may hold an older copy, and writing that back would undo a newer save. */
function blank() { return { owner: null, items: [], synced: 0, queue: [], seq: 0 }; }
function read() {
  try {
    var s = JSON.parse(localStorage.getItem(KEY) || 'null');
    if (s && Array.isArray(s.items)) {
      s.queue = Array.isArray(s.queue) ? s.queue : [];
      s.seq = s.seq || 0;
      return s;
    }
  } catch (e) {}
  return blank();
}
function write() { try { localStorage.setItem(KEY, JSON.stringify(state)); } catch (e) {} }
function fresh() { state = read(); }
function rand() {
  var c = window.crypto;
  if (c && c.getRandomValues) {
    var b = new Uint8Array(16); c.getRandomValues(b);
    return Array.prototype.map.call(b, function (x) { return ('0' + x.toString(16)).slice(-2); }).join('');
  }
  return (String(Math.random()).slice(2) + Date.now()).slice(0, 32);
}
/* A random id for this browser, so signed-out saves can be counted (which sizes people want). Nothing personal. */
function anonId() {
  try { var a = localStorage.getItem(ANON); if (!a) { a = rand(); localStorage.setItem(ANON, a); } return a; } catch (e) { return null; }
}

var state = read();

function find(p) {
  p = String(p);
  for (var i = 0; i < state.items.length; i++) if (state.items[i].p === p) return state.items[i];
  return null;
}

function announce() {
  paint(document);
  try { document.dispatchEvent(new CustomEvent('ee:wishlist', { detail: { items: state.items.slice() } })); } catch (e) {}
}
function changed() { write(); announce(); }

/* ---------- server ----------
   Signed in: every change goes into an outbox in localStorage BEFORE it is sent (keepalive, so a
   page reload mid-request still sends it), and leaves the outbox only on a 2xx. A network blip or
   a server error stays queued for the next page; a 4xx is dropped (it would fail again).
   Her account's list is adopted only when nothing newer is waiting to be sent.
   Signed out: one fire-and-forget count per change (anonymous), nothing to replay. */
function request(method, body) {
  var opts = { method: method, credentials: 'same-origin', keepalive: method === 'POST', headers: { 'Accept': 'application/json' } };
  if (body) { opts.headers['Content-Type'] = 'application/json'; opts.body = JSON.stringify(body); }
  return fetch(API, opts).then(function (r) {
    return r.json().catch(function () { return null; }).then(function (j) { return { status: r.status, json: j }; });
  });
}

/* The account's list is the truth once she is signed in. Keep handles we know if the server has none. */
function adopt(items) {
  fresh();
  if (state.queue.length) return; /* newer changes are still on their way: the next reply will carry them */
  var known = {};
  state.items.forEach(function (it) { known[it.p] = it; });
  state.items = (items || []).map(function (it) {
    return { p: String(it.p), v: it.v ? String(it.v) : null, h: it.h || (known[it.p] && known[it.p].h) || null, s: it.s || 'heart', at: it.at || new Date().toISOString() };
  });
  state.synced = Date.now();
  changed();
}

function dropJob(id) {
  fresh();
  state.queue = state.queue.filter(function (j) { return j.id !== id; });
  write();
}

function post(job) {
  return request('POST', { op: job.op, items: job.items }).then(function (res) {
    if (res.status >= 200 && res.status < 300) {
      dropJob(job.id);
      if (res.json && res.json.signedIn && Array.isArray(res.json.items)) adopt(res.json.items);
    } else if (res.status >= 400 && res.status < 500 && res.status !== 429) {
      dropJob(job.id); /* bad request or signature: replaying would fail the same way */
    }
    /* 429 / 5xx: stays in the outbox for the next page */
  }, function () { /* offline: stays in the outbox */ });
}

function send(op, items) {
  if (!ME) {
    request('POST', { op: op, items: items, a: anonId() }).catch(function () {});
    return Promise.resolve();
  }
  fresh();
  state.seq += 1;
  var job = { id: state.seq, op: op, items: items };
  state.queue.push(job);
  if (state.queue.length > 50) state.queue = state.queue.slice(-50);
  write();
  return post(job);
}

function replay() {
  fresh();
  var jobs = state.queue.slice();
  return jobs.reduce(function (chain, job) { return chain.then(function () { return post(job); }); }, Promise.resolve());
}

function pull() {
  return request('GET').then(function (res) {
    if (res.status === 200 && res.json && res.json.signedIn) adopt(res.json.items || []);
  }).catch(function () {});
}

function sync() {
  fresh();
  if (!ME) {
    /* She signed out on this device: her list stays on her account, not on a shared phone. */
    if (state.owner) { state = blank(); changed(); }
    return Promise.resolve();
  }
  if (state.owner !== ME) {
    /* Just signed in. A guest list on this browser joins her account. Another customer's list never carries over. */
    var guest = state.owner ? [] : state.items.slice();
    state = blank();
    state.owner = ME;
    state.items = guest;
    write();
    return guest.length ? send('merge', guest) : pull();
  }
  if (state.queue.length) return replay().then(function () { fresh(); return state.queue.length ? null : pull(); });
  if (Date.now() - (state.synced || 0) > STALE_MS) return pull();
  return Promise.resolve();
}

/* ---------- actions ---------- */
function add(item, source, meta) {
  fresh();
  var p = String(item.p);
  var at = new Date().toISOString();
  var it = find(p);
  var isNew = !it;
  if (it) {
    if (item.v) it.v = String(item.v);
    if (item.h) it.h = item.h;
  } else {
    it = { p: p, v: item.v ? String(item.v) : null, h: item.h || null, s: source || 'heart', at: at };
    state.items.unshift(it);
    if (state.items.length > MAX) state.items.length = MAX;
  }
  changed();
  send('add', [{ p: it.p, v: it.v, h: it.h, s: source || it.s, at: at }]);
  /* ads count a product once: a size picked later on a saved product is not a new save */
  track('add_to_wishlist', it, isNew ? (meta || {}) : null);
  return it;
}

function remove(p) {
  fresh();
  p = String(p);
  var it = find(p);
  if (!it) return;
  state.items = state.items.filter(function (x) { return x.p !== p; });
  changed();
  send('remove', [{ p: p }]);
  track('remove_from_wishlist', it);
}

function setSize(p, v) {
  fresh();
  var it = find(p);
  if (!it || !v || it.v === String(v)) return;
  it.v = String(v);
  changed();
  send('add', [{ p: it.p, v: it.v, h: it.h, s: 'page', at: new Date().toISOString() }]);
}

/* ---------- analytics (GA4 + Clarity, whichever is on the page) ----------
   A save also goes to Shopify's customer events as "ee_wishlist_add", for the
   "Empress wishlist to ads" custom pixel (Settings > Customer events), which
   sends it on to Meta, Pinterest and TikTok as Add to wishlist. The ad pixels
   live in Shopify's sandbox, so the theme can't call them directly. */
function track(name, it, meta) {
  try { if (window.gtag) window.gtag('event', name, { items: [{ item_id: 'shopify_AU_' + it.p + (it.v ? '_' + it.v : '') }] }); } catch (e) {}
  try { if (window.clarity) window.clarity('event', name); } catch (e) {}
  if (name !== 'add_to_wishlist' || !meta) return;
  try {
    var api = window.Shopify && window.Shopify.analytics;
    if (!api || typeof api.publish !== 'function') return;
    var price = meta && meta.price !== undefined && meta.price !== null && meta.price !== '' ? Number(meta.price) : null;
    api.publish('ee_wishlist_add', {
      productId: it.p,
      variantId: it.v || null,
      handle: it.h || null,
      title: (meta && meta.title) || null,
      price: price !== null && isFinite(price) ? price : null,
      currency: (window.Shopify.currency && window.Shopify.currency.active) || 'AUD',
      source: it.s || 'heart',
      eventId: 'wl-' + it.p + '-' + Date.now()
    });
  } catch (e) {}
}

/* What the ads events want that the saved item doesn't keep: the name and the price on the button. */
function metaOf(btn) {
  return { title: btn.getAttribute('data-title'), price: btn.getAttribute('data-price') };
}

/* ---------- the size she picked on a product page (never the XS the form defaults to) ---------- */
function chosenVariant(btn) {
  if (btn.getAttribute('data-variant-id')) return btn.getAttribute('data-variant-id'); /* one-size products */
  var formId = btn.getAttribute('data-form-id');
  var picker = formId
    ? document.querySelector('variant-picker[form-id="' + (window.CSS && CSS.escape ? CSS.escape(formId) : formId) + '"]')
    : null;
  if (!picker) {
    var scope = btn.closest('.shopify-section') || document;
    picker = scope.querySelector('variant-picker');
  }
  if (!picker) return null;
  var radios = picker.querySelectorAll('input[type="radio"][name]');
  var names = {};
  Array.prototype.forEach.call(radios, function (r) { names[r.name] = true; });
  if (Object.keys(names).length !== 1) return null;
  var checked = picker.querySelector('input[type="radio"][name]:checked');
  return checked && checked.getAttribute('data-sg-variant') || null;
}

/* ---------- painting ---------- */
function paint(root) {
  (root || document).querySelectorAll('[data-ee-wish]').forEach(function (btn) {
    var on = String(!!find(btn.getAttribute('data-product-id')));
    if (btn.getAttribute('aria-pressed') !== on) btn.setAttribute('aria-pressed', on);
  });
  var n = state.items.length;
  document.querySelectorAll('[data-ee-wish-count]').forEach(function (el) {
    el.textContent = n ? String(n) : '';
    el.hidden = !n;
  });
  document.documentElement.classList.toggle('ee-wish-has', n > 0);
}

/* ---------- toast ---------- */
var toastEl, toastTimer;
function toast(text, withLink) {
  if (!toastEl) {
    toastEl = document.createElement('div');
    toastEl.className = 'ee-wish-toast';
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    document.body.appendChild(toastEl);
  }
  toastEl.innerHTML = '';
  var span = document.createElement('span');
  span.textContent = text;
  toastEl.appendChild(span);
  if (withLink) {
    /* true = "View" the wishlist; {href, text} = any other link (the giveaway's sign in) */
    var a = document.createElement('a');
    a.href = withLink.href || PAGE;
    a.textContent = withLink.text || t('view', 'View');
    toastEl.appendChild(a);
  }
  toastEl.classList.add('is-on');
  clearTimeout(toastTimer);
  toastTimer = setTimeout(function () { toastEl.classList.remove('is-on'); }, 3200);
}

/* ---------- clicks ---------- */
function onHeart(btn) {
  var p = btn.getAttribute('data-product-id');
  if (!p) return;
  fresh();
  if (find(p)) {
    remove(p);
    toast(t('removed', 'Removed from wishlist'), false);
  } else {
    var source = btn.getAttribute('data-source') || 'heart';
    add({ p: p, h: btn.getAttribute('data-handle'), v: source === 'pdp' ? chosenVariant(btn) : btn.getAttribute('data-variant-id') }, source, metaOf(btn));
    /* Win your wishlist (signed out only): the toast invites her to sign in and enter */
    toast(t('saved', 'Saved to wishlist'), cfg.promo && cfg.promo.login ? { href: cfg.promo.login, text: cfg.promo.cta } : true);
    btn.classList.remove('is-pop'); void btn.offsetWidth; btn.classList.add('is-pop');
  }
}

/* Cart: save the line for later (with its size), then take it out of the bag the theme's own way.
   The save is in the outbox before the Remove click, so the cart page's reload can't lose it. */
function onLater(btn) {
  var p = btn.getAttribute('data-product-id');
  if (!p || btn.disabled) return;
  btn.disabled = true;
  add({ p: p, v: btn.getAttribute('data-variant-id'), h: btn.getAttribute('data-handle') }, 'cart', metaOf(btn));
  toast(t('laterDone', 'Saved for later'), true);
  /* The Remove link next to it: the theme's LineItemQuantity handles it (drawer refresh, or a reload on the cart page) */
  var box = btn.closest('line-item-quantity') || btn.closest('line-item, tr');
  var removeLink = box && box.querySelector('a[href*="/cart/change"]');
  if (removeLink) { removeLink.click(); return; }
  var url = btn.getAttribute('data-remove-url');
  if (url) window.location.href = url;
}

document.addEventListener('click', function (e) {
  var el = e.target;
  if (!el || !el.closest) return;
  var heart = el.closest('[data-ee-wish]');
  if (heart) {
    /* hearts sit on product cards inside links: never open the product */
    e.preventDefault();
    e.stopPropagation();
    onHeart(heart);
    return;
  }
  var later = el.closest('[data-ee-wish-later]');
  if (later) { e.preventDefault(); onLater(later); }
}, true);

/* Another tab changed the list */
window.addEventListener('storage', function (e) {
  if (e.key !== KEY) return;
  fresh();
  announce();
});

/* Back button: the restored page holds the list as it was when she left it */
window.addEventListener('pageshow', function (e) {
  if (!e.persisted) return;
  fresh();
  announce();
});

/* New hearts (quick buy, filters, cart drawer refresh): paint them, debounced */
var queued = false;
new MutationObserver(function (m) {
  if (queued) return;
  for (var i = 0; i < m.length; i++) {
    for (var j = 0; j < m[i].addedNodes.length; j++) {
      var n = m[i].addedNodes[j];
      if (n.nodeType === 1 && (n.hasAttribute('data-ee-wish') || n.querySelector('[data-ee-wish], [data-ee-wish-count]'))) {
        queued = true;
        setTimeout(function () { queued = false; paint(document); }, 60);
        return;
      }
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });

window.EEWish = {
  items: function () { fresh(); return state.items.slice(); },
  has: function (p) { return !!find(p); },
  get: function (p) { var it = find(p); return it ? { p: it.p, v: it.v, h: it.h, s: it.s, at: it.at } : null; },
  add: add,
  remove: remove,
  setSize: setSize,
  signedIn: !!ME,
  page: PAGE,
  text: t,
  toast: toast
};

/* EEWish.ready settles once a signed-in list has been fetched (straight away for guests or a fresh copy),
   so the wishlist page can show "Loading" rather than a flash of "Nothing saved yet". */
function boot() {
  paint(document);
  var go = function () {
    var done = sync();
    fresh();
    window.EEWish.synced = !ME || (state.owner === ME && !!state.synced);
    window.EEWish.ready = (done || Promise.resolve()).then(function () { window.EEWish.synced = true; }, function () { window.EEWish.synced = true; });
  };
  /* Refreshing her own saved list waits for the page to finish loading (except on the wishlist page),
     so the fetch never competes with the photos. A sign-in merge or a sign-out clear still runs at once. */
  fresh();
  var routine = !!ME && state.owner === ME;
  if (!routine || document.readyState === 'complete' || document.querySelector('[data-ee-wish-grid]')) go();
  else window.addEventListener('load', go, { once: true });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
